// Design agent: Spec 011 — MasqIP loses every entry on reload today, which is jarring for a tool
// people expect to keep coming back to (escalidraw-style local persistence), and it cannot be shared
// with a coworker as a link that reproduces the screen. This module is the single place that knows
// how the in-memory shape of the app's persisted state maps onto (a) a short, human-readable query
// string and (b) a versioned localStorage blob, so App and the two calculators never have to think
// about serialisation themselves.
// Developer agent: Every function here is pure and side-effect free except loadLocalState/
// saveLocalState/clearLocalState, which are the only three that touch `window.localStorage` — and
// even those never throw: private browsing, storage quotas, or a missing `window` (SSR-less here,
// but tests run under happy-dom) all degrade to "nothing was persisted" rather than crashing the app.
import { isValidInputFormat, parseCidr } from './ipMath';

// Design agent: Versioned so a future incompatible shape change can migrate or discard old data
// instead of feeding a mismatched structure into the calculators.
export const STORAGE_KEY = 'masqip.state.v1';

// Design agent: The single default layer handle mirrors VlsmCalculator's own default so a decoded
// state that never mentions method 2 falls back to exactly what a fresh visit already renders.
const DEFAULT_LAYER_HANDLES = [{ id: 'layer-0', prefix: 24, label: 'Layer 1' }];

const KNOWN_TABS = ['inspector', 'plan', 'about', 'showcase', 'reference'];

// Design agent: The canonical, component-ready shape every other function in this module reads or
// produces. Kept as one factory (rather than a shared frozen object) so callers can freely mutate
// their own copy without corrupting a module-level singleton.
// Developer agent: `inspector.entries`/`belongingIndices` and `vlsm.*` mirror the exact prop shapes
// IpInspector and VlsmCalculator already accept as `initialState`, so App can pass this object's
// slices straight through without any further translation.
export function createDefaultAppState() {
  return {
    tab: 'inspector',
    inspector: {
      entries: [],
      belongingIndices: [],
    },
    vlsm: {
      baseNetwork: '',
      hostInputs: [],
      gapSize: '',
      allowPointToPointLinks: true,
      mode: 'method1',
      pathSupernet: '',
      layerHandles: DEFAULT_LAYER_HANDLES.map((handle) => ({ ...handle })),
    },
  };
}

// Developer agent: Small helper so every "ignore this token" decision in the decoders below reads
// the same way — trims, then reports whether anything is left to parse.
const nonEmpty = (value) => typeof value === 'string' && value.trim() !== '';

// Design agent: Per spec, a malformed value like `?ip=abc` must be dropped rather than restored, so
// this reuses the exact same leniency IpInspector's own analysis already applies (parseCidr / bare
// IPv4 / ranges / masks — see isValidInputFormat) to decide what counts as "a real address", and
// drops anything else silently.
const isPlausibleInspectorToken = (token) => isValidInputFormat(token);

// Design agent: Turns a comma-separated list of inspector tokens into a clean array, dropping empty
// segments and anything that does not look like an address/CIDR/range at all.
// Developer agent: Shared by both the `ip` and `belong` URL params since they hold the same kind of
// value, just destined for different columns of the inspector.
function parseInspectorTokenList(rawValue) {
  if (!nonEmpty(rawValue)) {
    return [];
  }
  return rawValue
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token !== '' && isPlausibleInspectorToken(token));
}

// Design agent: Spec 011's `hosts` URL param packs one VLSM host row as `count[xmultiplier][:name]`.
// This is the single regex both the encoder and the decoder agree on, so the two can never drift.
// Developer agent: `count` and `multiplier` are digit-only groups; `name` is greedy and can contain
// almost anything (including further colons) since it is always the last thing in the token — the
// one character it cannot contain is a comma, which would be read as the start of the next row (see
// encodeHostInputs below, which strips commas out of names defensively for exactly this reason).
const HOST_ROW_PATTERN = /^(\d+)(?:x(\d+))?(?::([\s\S]*))?$/;

// Design agent: Parses the `hosts` URL param into the same `{hosts, multiplier, name}` row shape
// VlsmCalculator's own state already uses, so App can hand the result straight through as
// `initialState.hostInputs`.
// Developer agent: A row that does not match HOST_ROW_PATTERN at all (e.g. a stray empty segment
// from a trailing comma, or genuinely garbled input) is dropped silently; the rows around it are
// still restored.
function parseHostInputsList(rawValue) {
  if (!nonEmpty(rawValue)) {
    return [];
  }
  const rows = [];
  rawValue.split(',').forEach((token) => {
    const trimmed = token.trim();
    if (trimmed === '') {
      return;
    }
    const match = HOST_ROW_PATTERN.exec(trimmed);
    if (!match) {
      return;
    }
    const [, hosts, multiplier, name] = match;
    rows.push({
      hosts,
      multiplier: multiplier ?? '',
      name: name ?? '',
    });
  });
  return rows;
}

// Design agent: Mirrors parseHostInputsList in the other direction — encodes VlsmCalculator's live
// `hostInputs` rows into the compact `count[xmultiplier][:name]` tokens the URL carries.
// Developer agent: Rows whose `hosts` field is blank are the single trailing placeholder
// normaliseHostRows always keeps around for the "add another row" affordance — they carry no user
// data yet, so they are skipped rather than emitted as an empty token.
function encodeHostInputs(hostInputs) {
  return hostInputs
    .filter((row) => nonEmpty(row?.hosts))
    .map((row) => {
      let token = row.hosts.trim();
      if (nonEmpty(row.multiplier)) {
        token += `x${row.multiplier.trim()}`;
      }
      if (nonEmpty(row.name)) {
        // Developer agent: strip commas so a user-typed name can never be misread as a row
        // separator when the URL is later decoded (see HOST_ROW_PATTERN's comment above).
        const safeName = row.name.trim().replace(/,/g, '');
        if (safeName !== '') {
          token += `:${safeName}`;
        }
      }
      return token;
    })
    .join(',');
}

// Design agent: Spec 011's design note allows the method-2 `layers` param to carry only prefix
// lengths (e.g. "layers=24,28") and leave custom labels as a localStorage-only detail, since
// round-tripping labels through the URL too would need escaping rules for very little payoff — a
// shared link restores the same slider shape, just with the default "Layer 1", "Layer 2", ..."
// captions instead of whatever the sharer had renamed them to.
// Developer agent: Ids are regenerated sequentially ("layer-0", "layer-1", ...) rather than trusting
// anything from the URL, matching VlsmCalculator's own default numbering scheme.
function parseLayerPrefixesList(rawValue) {
  if (!nonEmpty(rawValue)) {
    return [];
  }
  const prefixes = rawValue
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token !== '')
    .map((token) => Number(token))
    .filter((prefix) => Number.isInteger(prefix) && prefix >= 0 && prefix <= 32);
  return prefixes.map((prefix, index) => ({
    id: `layer-${index}`,
    prefix,
    label: `Layer ${index + 1}`,
  }));
}

// Design agent: Tells the encoder whether the current layer handles are still exactly the app's
// untouched default, so a plan that never touched method 2 does not grow a redundant `layers=24`.
function isDefaultLayerHandles(layerHandles) {
  return (
    Array.isArray(layerHandles) &&
    layerHandles.length === 1 &&
    layerHandles[0]?.prefix === 24
  );
}

// Design agent: Detects whether the VLSM side of the app holds anything worth putting in the URL at
// all, so a user who only ever used the inspector never sees a `method=1&p2p=1` tail on their link.
function vlsmHasData(vlsmState) {
  return (
    nonEmpty(vlsmState.baseNetwork) ||
    nonEmpty(vlsmState.pathSupernet) ||
    vlsmState.hostInputs.some((row) => nonEmpty(row?.hosts)) ||
    vlsmState.mode === 'method2' ||
    vlsmState.allowPointToPointLinks !== true ||
    !isDefaultLayerHandles(vlsmState.layerHandles)
  );
}

// Design agent: Encodes one full app state snapshot into a short, human-readable query string (no
// leading "?"), exactly matching the param names and ordering from spec 011's examples. Any field
// that is empty/default is left out entirely so a mostly-empty session produces a short URL.
// Developer agent: Returns a plain string (via URLSearchParams#toString) rather than the
// URLSearchParams object itself, since every caller (App's history.replaceState, the unit tests)
// just wants the final text.
export function encodeState(appState) {
  const state = appState && typeof appState === 'object' ? appState : createDefaultAppState();
  const inspector = state.inspector || {};
  const vlsm = state.vlsm || {};
  const entries = Array.isArray(inspector.entries) ? inspector.entries : [];
  const belongingIndices = Array.isArray(inspector.belongingIndices) ? inspector.belongingIndices : [];

  const params = new URLSearchParams();

  params.set('tab', KNOWN_TABS.includes(state.tab) ? state.tab : 'inspector');

  const nonBelongingValues = entries
    .filter((value, index) => nonEmpty(value) && !belongingIndices.includes(index))
    .map((value) => value.trim());
  if (nonBelongingValues.length > 0) {
    params.set('ip', nonBelongingValues.join(','));
  }

  const belongingValues = entries
    .filter((value, index) => nonEmpty(value) && belongingIndices.includes(index))
    .map((value) => value.trim());
  if (belongingValues.length > 0) {
    params.set('belong', belongingValues.join(','));
  }

  if (vlsmHasData(vlsm)) {
    const relevantSupernet = vlsm.mode === 'method2' ? vlsm.pathSupernet : vlsm.baseNetwork;
    if (nonEmpty(relevantSupernet)) {
      params.set('net', relevantSupernet.trim());
    }

    const hostsToken = encodeHostInputs(Array.isArray(vlsm.hostInputs) ? vlsm.hostInputs : []);
    if (hostsToken !== '') {
      params.set('hosts', hostsToken);
    }

    params.set('method', vlsm.mode === 'method2' ? '2' : '1');
    params.set('p2p', vlsm.allowPointToPointLinks === false ? '0' : '1');

    if (vlsm.mode === 'method2' && Array.isArray(vlsm.layerHandles) && vlsm.layerHandles.length > 0) {
      params.set('layers', vlsm.layerHandles.map((handle) => handle.prefix).join(','));
    }
  }

  // Design agent: keep the shared link human-readable — "/" "," and ":" are legal in a query
  // string, so we undo URLSearchParams' percent-encoding of just those three characters.
  // Developer agent: decodeSearch feeds the string back through URLSearchParams, which accepts
  // both the raw and the encoded forms, so the round trip is unaffected.
  return params.toString().replace(/%2F/gi, '/').replace(/%2C/gi, ',').replace(/%3A/gi, ':');
}

// Design agent: The inverse of encodeState — reads a `location.search`-shaped string (with or
// without a leading "?") and returns a full app state object, defaulting every field that is
// missing or fails validation rather than ever throwing. Unknown/invalid params are ignored
// silently, per the spec, so a mistyped or hand-edited URL degrades to "whatever part still made
// sense" instead of a blank page.
export function decodeSearch(search) {
  const defaults = createDefaultAppState();
  const rawSearch = typeof search === 'string' ? search : '';
  const params = new URLSearchParams(rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch);

  const tabParam = params.get('tab');
  const tab = KNOWN_TABS.includes(tabParam) ? tabParam : defaults.tab;

  const ipEntries = parseInspectorTokenList(params.get('ip'));
  const belongEntries = parseInspectorTokenList(params.get('belong'));
  const entries = [...ipEntries, ...belongEntries];
  const belongingIndices = belongEntries.map((_, index) => ipEntries.length + index);

  const netParam = params.get('net');
  const hasValidNet = nonEmpty(netParam) && parseCidr(netParam.trim()) !== null;

  const methodParam = params.get('method');
  const mode = methodParam === '2' ? 'method2' : 'method1';

  const p2pParam = params.get('p2p');
  const allowPointToPointLinks = p2pParam === '0' ? false : defaults.vlsm.allowPointToPointLinks;

  const hostInputs = parseHostInputsList(params.get('hosts'));

  const layerHandles = parseLayerPrefixesList(params.get('layers'));

  return {
    tab,
    inspector: {
      entries,
      belongingIndices,
    },
    vlsm: {
      baseNetwork: mode === 'method1' && hasValidNet ? netParam.trim() : defaults.vlsm.baseNetwork,
      hostInputs: hostInputs.length > 0 ? hostInputs : defaults.vlsm.hostInputs,
      gapSize: defaults.vlsm.gapSize,
      allowPointToPointLinks,
      mode,
      pathSupernet: mode === 'method2' && hasValidNet ? netParam.trim() : defaults.vlsm.pathSupernet,
      layerHandles: layerHandles.length > 0 ? layerHandles : defaults.vlsm.layerHandles,
    },
  };
}

// Design agent: Reads the last snapshot saved to localStorage. Returns null on any failure
// (missing key, storage disabled, corrupted JSON) so callers can simply fall through to defaults.
// Developer agent: Deliberately does not attempt to validate/repair the parsed shape beyond a bare
// JSON.parse — App always merges this over createDefaultAppState() field by field, so a partially
// shaped object from an older, slightly different version of this module still degrades gracefully.
export function loadLocalState() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    return null;
  }
}

// Design agent: Persists one app state snapshot. Called from App's debounced write effect, never
// synchronously from a keystroke handler, so a burst of edits only ever costs one localStorage
// write per 300ms window.
// Developer agent: Swallows every error the same way writeExternalLookupsOptIn does (private
// browsing, quota) — losing persistence for this session is an acceptable degradation, crashing the
// app on every keystroke is not.
export function saveLocalState(appState) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(appState));
  } catch (error) {
    // Design agent: Storage unavailable — the current tab keeps working from in-memory state only.
  }
}

// Design agent: Backs the "Reset" action — wipes the persisted snapshot so the next load starts
// from defaults again.
export function clearLocalState() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // Design agent: Nothing to clean up if storage was never reachable in the first place.
  }
}
